'use client';

import { Button, Flex, IconButton, Tabs, TabsValueChangeDetails, Text } from '@chakra-ui/react';
import HistoryTab from '@features/positions/predictions/HistoryTab';
import OpenOrdersTab from '@features/positions/predictions/OpenOrdersTab';
import PortfolioCard from '@features/positions/predictions/PortfolioCard';
import PositionsTab from '@features/positions/predictions/PositionsTab';
import ProfitLossCard from '@features/positions/predictions/ProfitLossCard';
import { PredictionTab } from '@features/positions/predictions/types';
import { useHref } from 'hooks/useHref';
import { useRouter } from 'next/navigation';
import { memo, useCallback, useState } from 'react';
import { PlusIcon } from 'vectors';

const tabs: { label: string; value: PredictionTab }[] = [
  { label: 'Predictions', value: 'positions' },
  { label: 'Pending', value: 'pending' },
  { label: 'History', value: 'history' },
];

const PredictionsPage = () => {
  const [activeTab, setActiveTab] = useState<PredictionTab>('positions');
  const { predictionMarketHref } = useHref();
  const router = useRouter();

  const handleTabChange = useCallback((details: TabsValueChangeDetails) => {
    setActiveTab(details.value as PredictionTab);
  }, []);

  const onClickNewPosition = useCallback(() => {
    const href = predictionMarketHref();
    router.push(href);
  }, [predictionMarketHref]);

  return (
    <Flex flexDirection="column" h="full" p={4} gap={4}>
      <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
        <PortfolioCard balance={0} availableToTrade={0} dailyChange={0} dailyChangePercent={0} />
        <ProfitLossCard profitLoss={0} />
      </Flex>
      <Tabs.Root defaultValue={activeTab} variant="line" size="md" onValueChange={handleTabChange}>
        <Tabs.List borderColor="border.light">
          {tabs.map((tab) => (
            <Tabs.Trigger key={tab.value} value={tab.value}>
              <Text fontSize="sm" fontWeight="medium">
                {tab.label}
              </Text>
            </Tabs.Trigger>
          ))}
          <Flex ml="auto" gap={2} alignItems="center">
            <IconButton
              size="2xs"
              borderRadius="md"
              flexShrink={0}
              hideFrom="md"
              onClick={onClickNewPosition}
              aria-label="New prediction"
              ml="auto"
            >
              <PlusIcon />
            </IconButton>
            <Button
              size="2xs"
              borderRadius="md"
              flexShrink={0}
              hideBelow="md"
              onClick={onClickNewPosition}
              ml="auto"
            >
              New prediction <PlusIcon />
            </Button>
          </Flex>
        </Tabs.List>
        <Tabs.Content value="positions">
          <PositionsTab />
        </Tabs.Content>
        <Tabs.Content value="pending">
          <OpenOrdersTab />
        </Tabs.Content>
        <Tabs.Content value="history">
          <HistoryTab />
        </Tabs.Content>
      </Tabs.Root>
    </Flex>
  );
};

export default memo(PredictionsPage);
